import { Component, OnInit, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule, FileUpload } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, of } from 'rxjs';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { RegistrationStep, RegistrationStatus } from '../../../../core/services/registration-step.service';
import { FileValidationService } from '../../../../core/services/file-validation.service';
import { ContractRegistryService } from '../../../../backoffice/services/contract-registry.service';
import { DocumentType } from '../../../../backoffice/models/contract.model';
import { ProfessionalsWizardService } from '../../../services/professionals-wizard.service';
import { ProfessionalOperationalDetailsService } from '../../../../backoffice/services/axa-professional-data.service';
import { addSolCodeToFileName } from '../../../../core/utils/file-name.utils';

@Component({
  selector: 'app-professionals-contract-signature',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    FileUploadModule,
    ToastModule,
    ProgressSpinnerModule,
    TagModule,
    TooltipModule,
  ],
  providers: [MessageService],
  templateUrl: './professionals-contract-signature.component.html',
  styleUrls: ['./professionals-contract-signature.component.scss'],
})
export class ProfessionalsContractSignatureComponent implements OnInit {
  private readonly professionalsService = inject(ProfessionalsService);
  private readonly professionalsWizardService = inject(ProfessionalsWizardService);
  private readonly contractRegistryService = inject(ContractRegistryService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly fileValidationService = inject(FileValidationService);
  private readonly professionalOperationalDetailsService = inject(ProfessionalOperationalDetailsService);

  // Signals
  isLoading = signal(false);
  isDownloading = signal(false);
  isUploading = signal(false);
  isConfirming = signal(false);
  contractUrl = signal<string | null>(null);
  contractId = signal<string | null>(null);
  uploadedContractUrl = signal<string | null>(null);
  /** URL del documento controfirmato da AXA (FINAL), disponibile dopo l'attivazione */
  finalContractUrl = signal<string | null>(null);
  uploadedFile = signal<File | null>(null);
  professionalId = signal<string | null>(null);
  solCode = signal<string | null>(null);

  // Computed
  // Contratto completato quando in attesa di controfirma AXA
  isCompleted = computed(() => this.professionalsWizardService.currentRegistrationStep() === RegistrationStep.CONTRACT_COUNTERSIGNATURE);
  // Il professional può scaricare il contratto quando è nello step CONTRACT_SIGNATURE
  canDownload = computed(() => this.professionalsWizardService.currentRegistrationStep() === RegistrationStep.CONTRACT_SIGNATURE);
  
  // Il contratto è confermato solo se:
  // 1. Esiste il documento EXTERNALLY_SIGNED (uploadedContractUrl)
  // 2. E lo status NON è USER_INPUT (altrimenti l'utente può ancora caricare/modificare)
  isConfirmed = computed(() => {
    const hasDocument = !!this.uploadedContractUrl();
    const status = this.professionalsWizardService.currentRegistrationStatus();
    // Se non c'è documento, non è confermato
    if (!hasDocument) return false;
    // Se lo status è USER_INPUT, l'utente può ancora modificare (non confermato)
    // In tutti gli altri casi (PENDING, COMPLETED, etc.), è confermato
    return status !== RegistrationStatus.USER_INPUT;
  });
  
  // Il professional può caricare il contratto firmato quando è nello step CONTRACT_SIGNATURE
  canUpload = computed(() => this.professionalsWizardService.currentRegistrationStep() === RegistrationStep.CONTRACT_SIGNATURE && !this.isUploading() && !this.isConfirmed());
  hasUploadedContract = computed(() => !!this.uploadedFile());
  canConfirm = computed(() => this.hasUploadedContract() && !this.isConfirmed());
  
  // Computed tooltip for download button
  downloadTooltip = computed(() => {
    if (this.professionalsWizardService.currentRegistrationStep() !== RegistrationStep.CONTRACT_SIGNATURE) {
      return 'Funzione disponibile solo durante la fase di firma del contratto';
    }
    if (!this.contractUrl()) {
      return 'Contratto non ancora disponibile. Il backoffice deve prima caricare il documento.';
    }
    return 'Scarica il contratto in formato PDF da firmare';
  });
  
  // Computed tooltip message for confirm button
  confirmButtonTooltip = computed(() => {
    if (this.isConfirmed()) {
      return 'Il contratto è già stato confermato e inviato';
    }
    if (this.isConfirming()) {
      return 'Invio in corso...';
    }
    if (!this.hasUploadedContract()) {
      return 'Carica prima un file per abilitare la conferma';
    }
    return 'Conferma e invia il documento al backoffice';
  });

  // Max file size: 10MB
  readonly maxFileSize = 10 * 1024 * 1024;
  readonly acceptedFileTypes = '.pdf';

  ngOnInit(): void {
    this.loadProfessionalData();
  }

  /**
   * Load professional ID and contract data
   */
  private loadProfessionalData(): void {
    const profId = this.getCurrentProfessionalId();
    if (!profId) {
      console.error('❌ Professional ID not found in session');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fiduciario non trovato',
        life: 5000,
      });
      return;
    }

    this.professionalId.set(profId);
    this.loadSolCode(profId);

    // Carica prima lo step, poi sceglie DRAFT o ACTIVE di conseguenza
    this.professionalsWizardService.getCurrentStepAsObservable(profId).pipe(
      catchError(() => of(null))
    ).subscribe((stepRes) => {
      // Sincronizza lo stato wizard (step UI + currentRegistrationStep)
      if (stepRes) {
        this.professionalsWizardService['syncStateFromBackend'](stepRes);
      }
      this.professionalsWizardService.loadState(profId);

      const step = stepRes?.step as RegistrationStep | undefined;
      // Se lo step indica che il contratto è già stato attivato → ACTIVE, altrimenti DRAFT
      const contractStatus: 'DRAFT' | 'ACTIVE' =
        step === RegistrationStep.CONTRACT_COUNTERSIGNATURE ? 'ACTIVE' : 'DRAFT';

      console.log(`📍 Registration step: ${step} → loading contract with status=${contractStatus}`);
      this.loadContractData(profId, contractStatus);
    });
  }

  private loadSolCode(professionalId: string): void {
    this.professionalOperationalDetailsService.getOperationalDetails(professionalId).subscribe({
      next: (response: any) => {
        const data = response?.data ?? response;
        this.solCode.set(data?.otherFields?.solCode ?? data?.solCode ?? null);
      },
      error: () => this.solCode.set(null),
    });
  }

  /**
   * Load contract data for professional via professionals BFF
   */
  private loadContractData(professionalId: string, status: 'DRAFT' | 'ACTIVE'): void {
    this.isLoading.set(true);

    this.professionalsService.getProfessionalContract(professionalId, status).subscribe({
      next: (response) => {
        const contractId = response?.data;
        if (contractId) {
          console.log('✅ Contract ID found:', contractId);
          this.loadContractDetails(contractId);
        } else {
          console.warn('⚠️ No contract found for professional');
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Nessun contratto disponibile',
            life: 5000,
          });
          this.isLoading.set(false);
        }
      },
      error: (error: any) => {
        console.error('❌ Error loading contract:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il caricamento del contratto',
          life: 5000,
        });
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Load full contract details to get mainDocument
   */
  private loadContractDetails(contractId: string): void {
    this.contractRegistryService.getContractById(contractId).subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          const contract = response.data;
          this.contractId.set(contractId);
          
          // Check if professional has already uploaded signed document
          if (contract.mainDocument?.s3FileUrlExternallySigned) {
            this.uploadedContractUrl.set(contract.mainDocument.s3FileUrlExternallySigned);
            console.log('✅ Professional has already uploaded signed document (EXTERNALLY_SIGNED)');
          }

          // Salva separatamente l'URL del documento controfirmato AXA (FINAL)
          if (contract.mainDocument?.s3FileUrl) {
            this.finalContractUrl.set(contract.mainDocument.s3FileUrl);
            console.log('✅ AXA countersigned document (FINAL) available');
          }
          
          // Set document URL if exists
          // Priority: UNSIGNED (step 1 - da firmare) > FINAL (controfirmato) > EXTERNALLY_SIGNED (firmato dal professional)
          if (contract.mainDocument?.s3FileUrlUnsigned) {
            this.contractUrl.set(contract.mainDocument.s3FileUrlUnsigned);
            console.log('✅ Contract document (UNSIGNED) available for download');
          } else if (contract.mainDocument?.s3FileUrl) {
            this.contractUrl.set(contract.mainDocument.s3FileUrl);
            console.log('✅ Contract document (FINAL) available for download');
          } else if (contract.mainDocument?.s3FileUrlExternallySigned) {
            this.contractUrl.set(contract.mainDocument.s3FileUrlExternallySigned);
            console.log('✅ Contract document (EXTERNALLY_SIGNED) available for download');
          } else {
            this.contractUrl.set(null);
            console.warn('⚠️ Contract found but no document uploaded yet');
          }
        }
        this.isLoading.set(false);
      },
      error: (error: any) => {
        console.error('❌ Error loading contract details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il caricamento dei dettagli del contratto',
          life: 5000,
        });
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Scarica un documento per tipo (pattern comune)
   */
  private downloadDocumentByType(documentType: DocumentType, fileName: string, label: string): void {
    const contractIdValue = this.contractId();
    if (!contractIdValue) {
      this.messageService.add({ severity: 'warn', summary: 'Attenzione', detail: 'ID contratto non disponibile', life: 3000 });
      return;
    }
    this.contractRegistryService.downloadMainDocument(contractIdValue, documentType).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = addSolCodeToFileName(fileName, this.solCode());
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.messageService.add({ severity: 'success', summary: 'Successo', detail: `${label} scaricato con successo`, life: 3000 });
      },
      error: (error: any) => {
        console.error(`❌ Error downloading ${documentType}:`, error);
        this.messageService.add({ severity: 'error', summary: 'Errore Download', detail: error?.error?.message || `Errore durante il download del ${label.toLowerCase()}`, life: 5000 });
      },
    });
  }

  /**
   * Download contratto da firmare (UNSIGNED)
   */
  downloadContract(): void {
    if (!this.contractUrl()) {
      this.messageService.add({ severity: 'warn', summary: 'Documento non disponibile', detail: 'Il backoffice non ha ancora caricato il documento del contratto.', life: 5000 });
      return;
    }
    this.isDownloading.set(true);
    this.contractRegistryService.downloadMainDocument(this.contractId()!, DocumentType.UNSIGNED).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = addSolCodeToFileName(`contratto_${this.contractId()}.pdf`, this.solCode());
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.isDownloading.set(false);
        this.messageService.add({ severity: 'success', summary: 'Successo', detail: 'Contratto scaricato con successo', life: 3000 });
      },
      error: (error: any) => {
        console.error('❌ Error downloading contract:', error);
        this.isDownloading.set(false);
        const errorDetail = error?.status === 404 ? 'Il documento del contratto non è stato trovato.' : error?.error?.message || 'Errore durante il download del contratto';
        this.messageService.add({ severity: 'error', summary: 'Errore Download', detail: errorDetail, life: 5000 });
      },
    });
  }

  /**
   * Download contratto firmato dal fiduciario (EXTERNALLY_SIGNED)
   */
  downloadSignedContract(): void {
    if (!this.uploadedContractUrl()) {
      this.messageService.add({ severity: 'warn', summary: 'Attenzione', detail: 'Nessun contratto firmato disponibile per il download', life: 3000 });
      return;
    }
    this.downloadDocumentByType(DocumentType.EXTERNALLY_SIGNED, `contratto_firmato_${this.contractId()}.pdf`, 'Contratto firmato');
  }

  /**
   * Download contratto controfirmato da AXA (FINAL)
   */
  downloadFinalContract(): void {
    if (!this.finalContractUrl()) {
      this.messageService.add({ severity: 'warn', summary: 'Attenzione', detail: 'Il documento controfirmato da AXA non è ancora disponibile', life: 3000 });
      return;
    }
    this.downloadDocumentByType(DocumentType.FINAL, `contratto_controfirmato_${this.contractId()}.pdf`, 'Contratto controfirmato AXA');
  }
  async onFileUpload(event: any): Promise<void> {
    const file = event.files[0];
    if (!file) {
      return;
    }

    // 🔒 Validazione completa del file con FileValidationService
    // Include: size, MIME type, extension, magic numbers (file signature)
    // Verifica che sia un PDF reale, non un file mascherato
    const validation = await this.fileValidationService.validateDocument(file);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: '🔒 Validazione File Fallita',
        detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
        life: 6000
      });

      // Log dettagli per debugging
      if (validation.details) {
        console.warn('File validation failed:', validation.details);
      }

      event.files = [];
      return;
    }

    // File valido - Store file temporarily
    this.uploadedFile.set(file);
    this.messageService.add({
      severity: 'success',
      summary: 'File Selezionato',
      detail: 'File pronto per essere confermato',
      life: 3000,
    });
  }

  /**
   * Remove uploaded file before confirmation
   */
  removeUploadedFile(removeCallback: Function, file: File): void {
    removeCallback(file);
    this.uploadedFile.set(null);
    this.messageService.add({
      severity: 'info',
      summary: 'Rimosso',
      detail: 'File rimosso',
      life: 3000,
    });
  }

  /**
   * Confirm and upload signed contract to backend
   */
  confirmAndUpload(): void {
    const file = this.uploadedFile();
    const profId = this.professionalId();
    const contractIdValue = this.contractId();
    
    if (!file || !profId || !contractIdValue) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Dati mancanti per il caricamento',
        life: 3000,
      });
      return;
    }

    this.isConfirming.set(true);

    // Upload del contratto firmato dal fiduciario come EXTERNALLY_SIGNED
    this.contractRegistryService.uploadMainDocument(
      contractIdValue,
      file,
      profId,
      DocumentType.EXTERNALLY_SIGNED
    ).subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          this.uploadedContractUrl.set(response.data);
          
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Contratto firmato inviato con successo al backoffice',
            life: 5000,
          });

          // Update registration step to mark contract as signed
          this.updateRegistrationStep();
        }
        this.isConfirming.set(false);
      },
      error: (error: any) => {
        console.error('❌ Error uploading signed contract:', error);
        this.isConfirming.set(false);
        
        this.messageService.add({
          severity: 'error',
          summary: 'Errore Upload',
          detail: error?.error?.message || 'Errore durante il caricamento del contratto firmato',
          life: 5000,
        });
      },
    });
  }

  /**
   * Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Update registration step after upload
   */
  private updateRegistrationStep(): void {
    const profId = this.professionalId();
    if (!profId) {
      return;
    }

    // Advance to next step (CONTRACT_COUNTERSIGNATURE)
    this.professionalsWizardService.advanceToNextStep(profId);
    console.log('✅ Advancing to next registration step (CONTRACT_COUNTERSIGNATURE)');
  }

  /**
   * Permette al fiduciario di sostituire il documento firmato già caricato.
   * Resetta lo stato locale a CONTRACT_SIGNATURE (solo UI, nessuna chiamata BE)
   * così la sezione di upload si riabilita. Al prossimo confirmAndUpload il documento
   * EXTERNALLY_SIGNED viene sovrascritto e il passo ri-avanza a CONTRACT_COUNTERSIGNATURE.
   */
  replaceDocument(): void {
    this.uploadedContractUrl.set(null);
    this.uploadedFile.set(null);
    this.professionalsWizardService.overrideRegistrationStep(RegistrationStep.CONTRACT_SIGNATURE);
    this.messageService.add({
      severity: 'info',
      summary: 'Sostituzione abilitata',
      detail: 'Puoi ora caricare un nuovo documento firmato',
      life: 3000,
    });
  }

  /**
   * Get current professional ID from session storage
   */
  private getCurrentProfessionalId(): string | null {
    return sessionStorage.getItem('professional_id');
  }
}
